import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, LinearProgress, ListItem, ListItemButton, MenuItem, Switch, TextField, Tooltip, Typography, styled, switchClasses } from "@mui/material";
import FormSubmitButton from "@/components/forms/FormSubmitButton";
import Form from "@/components/forms/Form";
import { ISelectItem } from "@/components/forms/inputs/Select";
import { ConfigurationSourceData, ConfigurationSourceDataAllocation, Institution, Participant, ParticipantSourceDataAllocationUpdate, ConfigurationSourceDataAssignedAllocations, CoordinatingGroup, ParticipantSourceDataAllocation, Trial, MasterGroup, SourceDataAllocationType } from "@/api/dtos";
import { IReturn } from "@servicestack/client";
import { GroupedVirtuoso } from "react-virtuoso";
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import InputOnlyField from "@/components/forms/fields/inputOnly/InputOnlyField";
import Checkbox from "@/components/forms/inputs/Checkbox";
import { DateTime } from "luxon";
import { IFormSubmit, useFormActions } from "@ngt/forms-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-duotone-svg-icons";
import { FormError } from "@/errors";
import useConfigurationSourceDataAssignedAllocations from "@/hooks/data/useConfigurationSourceDataAssignedAllocations";
import { LoadingButton } from "@mui/lab";
import InstitutionsContext from "@/contexts/data/InstitutionsContext";
import ParticipantsContext from "@/contexts/data/ParticipantsContext";
import CoordinatingGroupsContext from "@/contexts/data/CoordinatingGroupsContext";
import TrialContext from "@/contexts/data/TrialContext";
import MasterGroupsContext from "@/contexts/data/MasterGroupsContext";
import { AppSettings } from "@/AppSettings";
import { Link } from "react-router-dom";


const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogTitle-root': {
        padding: theme.spacing(2),
        display: 'flex',
        gap: theme.spacing(2),
        justifyContent: 'space-between',
        alignItems: 'center',

        '& > div': {
        display: 'flex',
        alignItems: 'center'
        }
    },
    '& .MuiDialogContent-root': {
        padding: theme.spacing(0),
        minHeight: theme.spacing(12)
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));


const FormButtons = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end'
}));

const AllocationGroupBox = styled(ListItem)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    alignItems: 'center',
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    minHeight: theme.spacing(7.5)
}));

const AllocationBox = styled(ListItem)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(4),
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: theme.spacing(7.5)
}));

const AllocationName = styled(Box)(({ theme }) => ({
    flex: '1 1 auto'
}));

const AllocationEnrollmentBox = styled(Box)(({ theme }) => ({
    minWidth: theme.spacing(11.25),
    maxWidth: theme.spacing(11.25)
}));

const AllocationLinkButton = styled(Button)(({ theme }) => ({
    minHeight: theme.spacing(7.5),
    minWidth: theme.spacing(7.5),
    marginTop: theme.spacing(-2),
    marginBottom: theme.spacing(-2),
    marginRight: theme.spacing(-2),
}));

enum ListMode {
    Configuration = 1,
    Participants = 2
}

interface IConfigurationSourceDataAllocationsProps {
    allocations?: ConfigurationSourceDataAssignedAllocations;
    scroller?: HTMLElement;
    mode?: ListMode;
}

interface  IConfigurationSourceDataAllocation {
    type: AllocationType;
    trial?: Trial,
    masterGroup?: MasterGroup,
    coordinatingGroup?: CoordinatingGroup,
    institution?: Institution;
    participant?: Participant;
    link?: string;
    allocations?: ConfigurationSourceDataAllocation[];
    participantAllocation?: ParticipantSourceDataAllocation;
}

enum AllocationType {
    ConfigurationTrial = 1,
    ConfigurationMasterGroup = 2,
    ConfigurationCoordinatingGroup = 3,
    ConfigurationInstitution = 4,
    ConfigurationParticipant = 5,
    Participant
}

const getConfigurationAllocationGroups = (
    trial?: Trial,
    masterGroups?: Record<number, MasterGroup>,
    coordinatingGroups?: Record<number, CoordinatingGroup>,
    institutions?: Record<number, Institution>,
    participants?: Record<number, Participant>,
    allocations?: ConfigurationSourceDataAssignedAllocations['allocations']
) => {
    if (!institutions || !participants || !allocations) {
        return {
            groupCounts: [],
            groupNames: [],
            data: []
        };
    }

    let groupCounts: number[] = [];
    let groupNames: string[] = [];
    let data: IConfigurationSourceDataAllocation[] = [];

    const trialAllocations = allocations?.filter(x => 
        x.configurationTrialId && 
        !x.configurationMasterGroupId &&
        !x.configurationCoordinatingGroupId &&
        !x.configurationInstitutionId &&
        !x.configurationParticipantId
    ) ?? [];

    if (trialAllocations.length !== 0) {
        data.push({} as any);

        data.push({
            type: AllocationType.ConfigurationTrial,
            trial: trial,
            masterGroup: undefined,
            coordinatingGroup: undefined,
            institution: undefined,
            participant: undefined,
            link: `/${trial?.oid}/configuration/trial`,
            allocations: trialAllocations,
            participantAllocation: undefined
        })

        groupCounts.push(1);
        groupNames.push("Configuration - Trial")
    }

    const masterGroupAllocations = allocations?.filter(x => 
        !x.configurationTrialId && 
        x.configurationMasterGroupId &&
        !x.configurationCoordinatingGroupId &&
        !x.configurationInstitutionId &&
        !x.configurationParticipantId
    ) ?? [];

    if (masterGroupAllocations.length !== 0) {

        const groupedMasterGroupAllocationMap = masterGroupAllocations.reduce<Record<number, IConfigurationSourceDataAllocation>>(
            (state, current) => {
                if (!current.masterGroupId) {
                    return state;
                }

                if (!state[current.masterGroupId]) {
                    const masterGroup = masterGroups && current?.masterGroupId ?
                            masterGroups[current.masterGroupId] :
                            undefined;

                    state[current.masterGroupId] = {
                        type: AllocationType.ConfigurationMasterGroup,
                        trial: trial,
                        masterGroup: masterGroup,
                        coordinatingGroup: undefined,
                        institution: undefined,
                        participant: undefined,
                        link: `/${trial?.oid}/configuration/master-groups/${masterGroup?.code}`,
                        allocations: [],
                        participantAllocation: undefined
                    };
                }

                state[current.masterGroupId].allocations!.push(current);

                return state;
            },
            {}
        )

        const groupedMasterGroupAllocations = Object.keys(groupedMasterGroupAllocationMap).reduce<IConfigurationSourceDataAllocation[]>(
            (state, current) => {
                state.push((groupedMasterGroupAllocationMap as any)[current])

                return state;
            },
            []
        ).sort((a, b) => (a.masterGroup?.defaultName ?? a.masterGroup?.name ?? '') < (b.masterGroup?.defaultName ?? b.masterGroup?.name ?? '') ? -1 : 1);

        data.push({} as any);

        for (let masterGroupAllocation of groupedMasterGroupAllocations) {

            data.push(masterGroupAllocation)
        }

        groupCounts.push(groupedMasterGroupAllocations.length);
        groupNames.push("Configuration - Master Groups")
    }

    const coordinatingGroupAllocations = allocations?.filter(x => 
        !x.configurationTrialId && 
        !x.configurationMasterGroupId &&
        x.configurationCoordinatingGroupId &&
        !x.configurationInstitutionId &&
        !x.configurationParticipantId
    ) ?? [];

    if (coordinatingGroupAllocations.length !== 0) {
        
        const groupedCoordinatingGroupAllocationMap = coordinatingGroupAllocations.reduce<Record<number, IConfigurationSourceDataAllocation>>(
            (state, current) => {
                if (!current.coordinatingGroupId) {
                    return state;
                }

                if (!state[current.coordinatingGroupId]) {
                    const masterGroup = masterGroups && current?.masterGroupId ?
                            masterGroups[current.masterGroupId] :
                            undefined;

                    const coordinatingGroup = coordinatingGroups && current?.coordinatingGroupId ?
                            coordinatingGroups[current.coordinatingGroupId] :
                            undefined;

                    state[current.coordinatingGroupId] = {
                        type: AllocationType.ConfigurationCoordinatingGroup,
                        trial: trial,
                        masterGroup: masterGroup,
                        coordinatingGroup: coordinatingGroup,
                        institution: undefined,
                        participant: undefined,
                        link: `/${trial?.oid}/configuration/coordinating-groups/${coordinatingGroup?.code}`,
                        allocations: [],
                        participantAllocation: undefined
                    };
                }

                state[current.coordinatingGroupId].allocations!.push(current);

                return state;
            },
            {}
        )

        const groupedCoordinatingGroupAllocations = Object.keys(groupedCoordinatingGroupAllocationMap).reduce<IConfigurationSourceDataAllocation[]>(
            (state, current) => {
                state.push((groupedCoordinatingGroupAllocationMap as any)[current])

                return state;
            },
            []
        ).sort((a, b) => (a.coordinatingGroup?.defaultName ?? a.coordinatingGroup?.name ?? '') < (b.coordinatingGroup?.defaultName ?? b.coordinatingGroup?.name ?? '') ? -1 : 1);

        data.push({} as any);

        for (let coordinatingGroupAllocation of groupedCoordinatingGroupAllocations) {

            data.push(coordinatingGroupAllocation)
        }

        groupCounts.push(groupedCoordinatingGroupAllocations.length);
        groupNames.push("Configuration - Coordinating Groups")
    }

    const institutionAllocations = allocations?.filter(x => 
        !x.configurationTrialId && 
        !x.configurationMasterGroupId &&
        !x.configurationCoordinatingGroupId &&
        x.configurationInstitutionId &&
        !x.configurationParticipantId
    ) ?? [];

    if (institutionAllocations.length !== 0) {
        
        const groupedInstitutionAllocationMap = institutionAllocations.reduce<Record<number, IConfigurationSourceDataAllocation>>(
            (state, current) => {
                if (!current.institutionId) {
                    return state;
                }

                if (!state[current.institutionId]) {
                    const masterGroup = masterGroups && current?.masterGroupId ?
                            masterGroups[current.masterGroupId] :
                            undefined;

                    const coordinatingGroup = coordinatingGroups && current?.coordinatingGroupId ?
                            coordinatingGroups[current.coordinatingGroupId] :
                            undefined;

                    const institution = institutions && current?.institutionId ?
                            institutions[current.institutionId] :
                            undefined;

                    state[current.institutionId] = {
                        type: AllocationType.ConfigurationInstitution,
                        trial: trial,
                        masterGroup: masterGroup,
                        coordinatingGroup: coordinatingGroup,
                        institution: institution,
                        participant: undefined,
                        link: `/${trial?.oid}/configuration/institutions/${institution?.code}`,
                        allocations: [],
                        participantAllocation: undefined
                    };
                }

                state[current.institutionId].allocations!.push(current);

                return state;
            },
            {}
        )

        const groupedInstitutionAllocations = Object.keys(groupedInstitutionAllocationMap).reduce<IConfigurationSourceDataAllocation[]>(
            (state, current) => {
                state.push((groupedInstitutionAllocationMap as any)[current])

                return state;
            },
            []
        ).sort((a, b) => (a.institution?.defaultName ?? a.institution?.name ?? '') < (b.institution?.defaultName ?? b.institution?.name ?? '') ? -1 : 1);

        data.push({} as any);

        for (let institutionAllocation of groupedInstitutionAllocations) {

            data.push(institutionAllocation)
        }

        groupCounts.push(groupedInstitutionAllocations.length);
        groupNames.push("Configuration - Institutions")
    }

    const participantAllocations = allocations?.filter(x => 
        !x.configurationTrialId && 
        !x.configurationMasterGroupId &&
        !x.configurationCoordinatingGroupId &&
        !x.configurationInstitutionId &&
        x.configurationParticipantId
    ) ?? [];

    if (participantAllocations.length !== 0) {
        const groupedParticipantAllocationMap = participantAllocations.reduce<Record<number, IConfigurationSourceDataAllocation>>(
            (state, current) => {
                if (!current.participantId) {
                    return state;
                }

                if (!state[current.participantId]) {
                    const masterGroup = masterGroups && current?.masterGroupId ?
                            masterGroups[current.masterGroupId] :
                            undefined;

                    const coordinatingGroup = coordinatingGroups && current?.coordinatingGroupId ?
                            coordinatingGroups[current.coordinatingGroupId] :
                            undefined;

                    const institution = institutions && current?.institutionId ?
                            institutions[current.institutionId] :
                            undefined;

                    const participant = participants && current?.participantId ?
                            participants[current.participantId] :
                            undefined;

                    state[current.participantId] = {
                        type: AllocationType.ConfigurationParticipant,
                        trial: trial,
                        masterGroup: masterGroup,
                        coordinatingGroup: coordinatingGroup,
                        institution: institution,
                        participant: participant,
                        link: `/${trial?.oid}/configuration/institutions/${institution?.code}/participants/${participant?.studyNumber}`,
                        allocations: [],
                        participantAllocation: undefined
                    };
                }

                state[current.participantId].allocations!.push(current);

                return state;
            },
            {}
        )

        const groupedParticipantAllocations = Object.keys(groupedParticipantAllocationMap).reduce<IConfigurationSourceDataAllocation[]>(
            (state, current) => {
                state.push((groupedParticipantAllocationMap as any)[current])

                return state;
            },
            []
        ).sort((a, b) => (a.participant?.studyNumber ?? '') < (b.participant?.studyNumber ?? '') ? -1 : 1);

        data.push({} as any);

        for (let participantAllocation of groupedParticipantAllocations) {

            data.push(participantAllocation)
        }

        groupCounts.push(groupedParticipantAllocations.length);
        groupNames.push("Configuration - Participants")
    }

    
    return {
        groupCounts,
        groupNames,
        data
    }
}



const getParticipantAllocationGroups = (
    trial?: Trial,
    masterGroups?: Record<number, MasterGroup>,
    coordinatingGroups?: Record<number, CoordinatingGroup>,
    institutions?: Record<number, Institution>,
    participants?: Record<number, Participant>,
    allocations?: ConfigurationSourceDataAssignedAllocations['participantAllocations']
) => {
    if (!institutions || !participants || !allocations) {
        return {
            groupCounts: [],
            groupNames: [],
            data: []
        };
    }

    let groupCounts: number[] = [];
    let groupNames: string[] = [];
    let data: IConfigurationSourceDataAllocation[] = [];

    
    if (allocations && allocations.length !== 0) {

        for (let participantAllocation of allocations) {
            const institution = institutions && participantAllocation?.institutionId ?
                    institutions[participantAllocation.institutionId] :
                    undefined;

            const participant = participants && participantAllocation?.participantId ?
                    participants[participantAllocation.participantId] :
                    undefined;

            data.push({
                type: AllocationType.Participant,
                trial: trial,
                masterGroup: masterGroups && participantAllocation?.masterGroupId ?
                    masterGroups[participantAllocation.masterGroupId] :
                    undefined,
                coordinatingGroup: coordinatingGroups && participantAllocation?.coordinatingGroupId ?
                    coordinatingGroups[participantAllocation.coordinatingGroupId] :
                    undefined,
                institution: institution,
                participant: participant,
                link: `/${trial?.oid}/institutions/${institution?.code}/participants/${participant?.studyNumber}`,
                allocations: undefined,
                participantAllocation: participantAllocation
            })
        }

        data.sort((a, b) => (a.participant?.studyNumber ?? '') < (b.participant?.studyNumber ?? '') ? -1 : 1);

        data = [{} as any, ...data];
        groupCounts.push(allocations.length);
        groupNames.push("Participants")
    }

    
    return {
        groupCounts,
        groupNames,
        data
    }
}

const ConfigurationSourceDataAllocations: FC<IConfigurationSourceDataAllocationsProps> = ({
    allocations,
    scroller,
    mode
}) => {
    
    const { data: trial } = useContext(TrialContext);
    const { idMap: masterGroups } = useContext(MasterGroupsContext);
    const { idMap: coordinatingGroups } = useContext(CoordinatingGroupsContext);
    const { idMap: institutions } = useContext(InstitutionsContext);
    const { idMap: participants } = useContext(ParticipantsContext);

    const selectedListMode = mode ?? ListMode.Configuration;

    const listData = useMemo(() => {

        return {
            [ListMode.Configuration]: getConfigurationAllocationGroups(trial, masterGroups, coordinatingGroups, institutions, participants, allocations?.allocations),
            [ListMode.Participants]: getParticipantAllocationGroups(trial, masterGroups, coordinatingGroups, institutions, participants, allocations?.participantAllocations)
        };
    }, [participants, institutions, coordinatingGroups, masterGroups, trial, allocations])

    if (!scroller) {
        return null;
    }

    return (
        <GroupedVirtuoso
            groupCounts={listData[selectedListMode].groupCounts}
            groupContent={(index) => {
                return (
                    <AllocationGroupBox>
                        {listData[selectedListMode].groupNames[index]}
                    </AllocationGroupBox>
                );
            }}
            data={listData[selectedListMode].data}
            customScrollParent={scroller}
            itemContent={(index, groupIndex, item, context) => {
                return (
                    <Box>
                        {
                            item.type === AllocationType.ConfigurationTrial && (
                                <AllocationBox>
                                    <AllocationName>
                                        {item.trial?.name}
                                    </AllocationName>
                                    <Tooltip title="Go to Configuration" followCursor placement="left" arrow>
                                        <AllocationLinkButton
                                            //@ts-ignore-next-line
                                            component={"a"}
                                            href={item.link}
                                            target="_blank"
                                        >
                                            <FontAwesomeIcon icon={AppSettings.icons.externalLink} fixedWidth size="xl" />
                                        </AllocationLinkButton>
                                    </Tooltip>
                                </AllocationBox>
                            )
                        }
                        {
                            item.type === AllocationType.ConfigurationMasterGroup && (
                                <AllocationBox>
                                    <AllocationName>
                                        {item.masterGroup?.name}
                                    </AllocationName>
                                    <Tooltip title="Go to Configuration" followCursor placement="left" arrow>
                                        <AllocationLinkButton
                                            //@ts-ignore-next-line
                                            component={"a"}
                                            href={item.link}
                                            target="_blank"
                                        >
                                            <FontAwesomeIcon icon={AppSettings.icons.externalLink} fixedWidth size="xl" />
                                        </AllocationLinkButton>
                                    </Tooltip>
                                </AllocationBox>
                            )
                        }
                        {
                            item.type === AllocationType.ConfigurationCoordinatingGroup && (
                                <AllocationBox>
                                    <AllocationName>
                                        {item.coordinatingGroup?.name}
                                    </AllocationName>
                                    <Tooltip title="Go to Configuration" followCursor placement="left" arrow>
                                        <AllocationLinkButton
                                            //@ts-ignore-next-line
                                            component={"a"}
                                            href={item.link}
                                            target="_blank"
                                        >
                                            <FontAwesomeIcon icon={AppSettings.icons.externalLink} fixedWidth size="xl" />
                                        </AllocationLinkButton>
                                    </Tooltip>
                                </AllocationBox>
                            )
                        }
                        {
                            item.type === AllocationType.ConfigurationInstitution && (
                                <AllocationBox>
                                    <AllocationName>
                                        {item.institution?.name}
                                    </AllocationName>
                                    <Tooltip title="Go to Configuration" followCursor placement="left" arrow>
                                        <AllocationLinkButton
                                            //@ts-ignore-next-line
                                            component={"a"}
                                            href={item.link}
                                            target="_blank"
                                        >
                                            <FontAwesomeIcon icon={AppSettings.icons.externalLink} fixedWidth size="xl" />
                                        </AllocationLinkButton>
                                    </Tooltip>
                                </AllocationBox>
                            )
                        }
                        {
                            item.type === AllocationType.ConfigurationParticipant && (
                                <AllocationBox>
                                    <AllocationName>
                                        {item.participant?.studyNumber}
                                    </AllocationName>
                                    <Tooltip title="Go to Configuration" followCursor placement="left" arrow>
                                        <AllocationLinkButton
                                            //@ts-ignore-next-line
                                            component={"a"}
                                            href={item.link}
                                            target="_blank"
                                        >
                                            <FontAwesomeIcon icon={AppSettings.icons.externalLink} fixedWidth size="xl" />
                                        </AllocationLinkButton>
                                    </Tooltip>
                                </AllocationBox>
                            )
                        }
                        {
                            item.type === AllocationType.Participant && (
                                <AllocationBox>
                                    <AllocationName>
                                        {item.participant?.studyNumber}
                                    </AllocationName>
                                    <Tooltip title="Enrollment Date" followCursor placement="left" arrow>
                                        <AllocationEnrollmentBox>
                                            {item.participant?.enrolmentDate && DateTime.fromISO(item.participant.enrolmentDate).toFormat('dd/MM/yyyy')}
                                        </AllocationEnrollmentBox>
                                    </Tooltip>
                                    <Tooltip title="Go to Participant" followCursor placement="left" arrow>
                                        <AllocationLinkButton
                                            //@ts-ignore-next-line
                                            component={"a"}
                                            href={item.link}
                                            target="_blank"
                                        >
                                            <FontAwesomeIcon icon={AppSettings.icons.externalLink} fixedWidth size="xl" />
                                        </AllocationLinkButton>
                                    </Tooltip>
                                </AllocationBox>
                            )
                        }
                        <Divider />
                    </Box>
                )
            }}
        />
    )
}

interface IConfigurationSourceDataDeleteDialogProps<TData extends object = any> {
    configurationSourceData?: ConfigurationSourceData;
    sourceDataOptions?: Record<number, ConfigurationSourceData>;
    institutions?: Record<number, Institution>;
    participants?: Record<number, Participant>;
    open?: boolean;
    onClose?: () => void;
    deleteConfiguration: (configurationSourceDataId?: number) => Promise<boolean>;
}

const ConfigurationSourceDataDeleteDialog = <TData extends object = any>({
    configurationSourceData,
    institutions,
    participants,
    sourceDataOptions,
    open,
    onClose,
    deleteConfiguration
}: IConfigurationSourceDataDeleteDialogProps<TData>) => {
    const [scroller, setScroller] = useState<HTMLElement>();
    const [listMode, setListMode] = useState(ListMode.Configuration);

    const [deleting, setDeleting] = useState(false);    

    const {data: allocations, loading: loadingAllocations, validating: validatingAllocations, refresh: refreshAllocations} = useConfigurationSourceDataAssignedAllocations(open ? configurationSourceData?.id : undefined);

    const loading = loadingAllocations || validatingAllocations;

    const hasAllocations = useMemo(() => {
        if (!allocations ||
            !allocations.allocations ||
            allocations.allocations.length === 0 ||
            !allocations.participantAllocations ||
            allocations.participantAllocations.length === 0
        )
        {
            return false;
        }

        return true;
    }, [allocations])

    return (
        <StyledDialog
            //onClose={handleClose}
            aria-labelledby="customized-dialog-title"
            open={open ?? false}
            maxWidth="md"
            fullWidth
            scroll="paper"
        >
            <DialogTitle>
                Delete Configuration
            </DialogTitle>
            <DialogContent sx={{flex: '0 0 auto'}}>
                <Divider />
                {
                    open && (
                        <Box
                            sx={{
                                padding: 3
                            }}
                        >
                            <Grid container spacing={1}>
                                <Grid item minWidth={140}>
                                    <strong>Name</strong>
                                </Grid>
                                <Grid item>
                                    {configurationSourceData?.name}
                                </Grid>
                            </Grid>
                            <Grid container spacing={1} marginTop={1}>
                                <Grid item minWidth={140}>
                                    <strong>Description</strong>
                                </Grid>
                                <Grid item>
                                    {configurationSourceData?.description}
                                </Grid>
                            </Grid>
                        </Box>
                    )
                }
                {
                    open && !loading && !hasAllocations && (
                        <>
                            <Divider />
                            <Alert severity="warning">
                                <Typography>Are you sure you wish to delete the configuration?</Typography>
                            </Alert>
                            <Divider />
                        </>
                    )
                }
                {
                    open && !loadingAllocations && hasAllocations && (
                        <>
                            <Divider />
                            <Alert 
                                severity="warning"
                                action={
                                    <Box
                                        sx={{
                                            alignSelf: 'center'
                                        }}
                                    >
                                        <TextField 
                                            sx={{mr: 2}}
                                            label="Show"
                                            select
                                            size="small"
                                            value={listMode}
                                            onChange={(e) => {
                                                setListMode(e.target.value as any)
                                            }}
                                        >
                                            <MenuItem value={ListMode.Configuration}>Configuration</MenuItem>
                                            <MenuItem value={ListMode.Participants}>Participants</MenuItem>
                                        </TextField>
                                        <LoadingButton
                                            color="inherit"
                                            onClick={refreshAllocations}
                                        >
                                            Refresh
                                        </LoadingButton>
                                    </Box>
                                }
                            >
                                <Typography gutterBottom>The configuration is in use and cannot be deleted!</Typography>
                                <Typography>Remove the source data configuration from the configurations listed below to enable deletion.</Typography>
                            </Alert>
                        </>
                    )
                }
                {
                    open && loading &&  (
                        <>
                            <Divider />
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 3,
                                    gap: 3,
                                    flexDirection: 'column'
                                }}
                            >
                                <CircularProgress />
                                <Typography>
                                    Checking configuration allocation...
                                </Typography>
                            </Box>
                            <Divider />
                        </>
                    )
                }
            </DialogContent>
            {
                open && !loading && hasAllocations && (
                    <DialogContent dividers ref={(ref) => setScroller(ref as HTMLElement)}> 
                                <ConfigurationSourceDataAllocations
                                    allocations={allocations}
                                    scroller={scroller}
                                    mode={listMode}
                                />
                    </DialogContent>
                )
            }
            <DialogActions>
                    <FormButtons>
                        <LoadingButton
                            variant="contained"
                            color="error"
                            loading={loading || deleting}
                            disabled={hasAllocations}
                            onClick={async () => {
                                setDeleting(true);

                                try {
                                    await deleteConfiguration(configurationSourceData?.id);

                                    onClose?.();
                                }
                                finally {
                                    setDeleting(false);
                                }
                            }}
                        >
                            Delete
                        </LoadingButton>
                        { 
                            onClose && (
                                <Button
                                    variant="contained"
                                    color="inherit"
                                    onClick={onClose}
                                >
                                    Cancel
                                </Button>
                            )
                        }
                    </FormButtons>
            </DialogActions>
        </StyledDialog>
    );
}

export default ConfigurationSourceDataDeleteDialog;